import React, { useCallback, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { useHeader, useEndVideoChat } from 'hooks';
import { ContributionType } from 'helpers/constants';
import { fetchUpcomingCreatedWithType } from 'actions/contributions';

import Loader from 'components/UI/Loader';
import Banner from 'components/Banner/Banner';
import ContributionsTable from 'components/Tables/Contributions';
import SessionsTable from 'components/Tables/Sessions';

import './LiveCoursesPage.scss';

const WorkshopPage = ({ userId, loading, error, contributions, closestClassForBanner, sessions, getContributions }) => {
  useHeader('Workshops');

  const renderList = useCallback(async () => {
    getContributions(userId, ContributionType.contributionCourse);
  }, [getContributions, userId]);

  const { Popup } = useEndVideoChat(renderList);

  const filteredContributions = useMemo(() => contributions?.filter(item => item?.isWorkshop === true) || [], [
    contributions,
  ]);
  const filteredSessions = useMemo(() => sessions?.filter(item => item?.isWorkshop === true) || [], [sessions]);

  const [prevData, setPrevData] = useState({
    contributions: filteredContributions,
    sessions: filteredSessions,
  });

  useEffect(() => {
    if (!loading) {
      setPrevData({
        contributions: filteredContributions,
        sessions: filteredSessions,
      });
    }
  }, [filteredContributions, filteredSessions, loading]);

  useEffect(() => {
    renderList();
  }, [renderList]);

  if (!prevData?.contributions?.length && !loading) {
    return <div>There are no courses yet.</div>;
  }

  return (
    <div className="tables-container">
      {loading && <Loader />}
      <div>
        <Banner type="closestSession" closestClassForBanner={closestClassForBanner} />
        {prevData?.contributions?.length > 0 && (
          <>
            <h2 className="table__title">My Services</h2>
            <ContributionsTable
              rows={loading ? prevData?.contributions : filteredContributions}
              refreshList={renderList}
            />
          </>
        )}
      </div>
      <div>
        {prevData?.sessions?.length > 0 && (
          <>
            <h2 className="table__title">Upcoming Sessions</h2>
            <SessionsTable rows={loading ? prevData?.sessions : filteredSessions} refreshList={renderList} />
          </>
        )}
      </div>

      <Popup />
    </div>
  );
};

const mapStateToProps = ({ upcomingCreatedWithType: { loading, error, upcomingCreatedWithType }, account }) => ({
  userId: account?.user?.id,
  loading,
  error,
  contributions: upcomingCreatedWithType?.contributions || [],
  sessions: upcomingCreatedWithType?.upcomingSessions || [],
  closestClassForBanner: upcomingCreatedWithType?.closestClassForBanner || {},
});

const actions = {
  getContributions: fetchUpcomingCreatedWithType,
};

WorkshopPage.propTypes = {
  userId: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      message: PropTypes.string,
    }),
  ]),
  contributions: PropTypes.arrayOf(PropTypes.object).isRequired,
  sessions: PropTypes.arrayOf(PropTypes.object).isRequired,
  getContributions: PropTypes.func.isRequired,
  closestClassForBanner: PropTypes.shape({}).isRequired,
};

WorkshopPage.defaultProps = {
  error: null,
};

export default connect(mapStateToProps, actions)(WorkshopPage);
